import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LineReveal } from "../motion/Reveal";
import { Quote } from "lucide-react";
import { useLang, UI } from "@/lib/lang";

export default function Testimonials({ testimonials = [] }) {
  const { t } = useLang();
  const [i, setI] = useState(0);
  const n = testimonials.length;

  useEffect(() => {
    if (n < 2) return;
    const id = setInterval(() => setI((v) => (v + 1) % n), 6500);
    return () => clearInterval(id);
  }, [n]);

  if (n === 0) return null;
  const tt = testimonials[i];

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative py-20 md:py-40 bg-[#0A0A0A] border-y border-white/10 overflow-hidden"
    >
      <div className="absolute inset-0 grid-blueprint opacity-20" />
      <div className="absolute -top-40 -left-40 w-[600px] h-[600px] glow-blue opacity-40" />

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 md:px-14">
        <div className="grid grid-cols-12 gap-6 mb-16">
          <div className="col-span-12 md:col-span-3">
            <p className="num-tick mb-4">{t(UI.section05)}</p>
          </div>
          <div className="col-span-12 md:col-span-9">
            <h2 className="font-display uppercase text-white text-4xl md:text-6xl lg:text-7xl leading-[0.95]" data-testid="testimonials-title">
              <LineReveal text={t(UI.testH1)} />
              <span className="block txt-stroke">
                <LineReveal text={t(UI.testH2)} delay={0.2} />
              </span>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6 items-start">
          <div className="col-span-12 md:col-span-3">
            <Quote size={48} className="text-[#1E7FFF]" strokeWidth={1.5} />
            <div className="mt-8 space-y-3">
              {testimonials.map((it, idx) => (
                <button
                  key={idx}
                  onClick={() => setI(idx)}
                  className={`block w-full text-left num-tick py-2 border-l-2 pl-4 transition-all ${
                    idx === i ? "border-[#1E7FFF] text-white" : "border-white/10 text-white/40 hover:text-white/70"
                  }`}
                  data-testid={`testimonial-nav-${idx}`}
                >
                  {String(idx + 1).padStart(2, "0")} — {(it.name || "").split(" ")[0]}
                </button>
              ))}
            </div>
          </div>
          <div className="col-span-12 md:col-span-9">
            <AnimatePresence mode="wait">
              <motion.blockquote
                key={i}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                data-testid="testimonial-quote"
              >
                <p className="font-display uppercase text-white text-3xl md:text-5xl lg:text-6xl leading-[1.05]">
                  “{t(tt.quote)}”
                </p>
                <footer className="mt-8 flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#1E7FFF]/10 border border-[#1E7FFF]/40 flex items-center justify-center font-mono-x text-[#1E7FFF]">
                    {tt.name?.charAt(0) || "•"}
                  </div>
                  <div>
                    <p className="text-white font-mono-x uppercase tracking-widest text-sm">{tt.name}</p>
                    <p className="text-white/50 text-sm mt-1">{t(tt.role)}</p>
                  </div>
                </footer>
              </motion.blockquote>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
